import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatTimepicker,
  MatTimepickerInput,
  MatTimepickerToggle,
} from '@angular/material/timepicker';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RdsButtonComponent,
  RdsChipComponent,
  RdsDialogService,
  RdsFormFieldComponent,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsInputDirective,
  RdsLabelDirective,
  RdsLinkComponent,
  RdsProgressSpinnerComponent,
  RdsRadioButtonComponent,
  RdsRadioGroupDirective,
  RdsSwitchComponent,
  RdsTabComponent,
  RdsTabsComponent,
  RdsTooltipDirective,
} from '@rds/angular-components';
import { format } from 'date-fns';
import { delay, filter, finalize, of, switchMap, tap } from 'rxjs';

import {
  ManageSpaceConfigurationModel,
  RoleType,
  RoomServicesModel,
  RoomSettingModel,
  RoomSettingsBlockingTimeModel,
  RoomSettingsDurationRangeModel,
} from '@bookly/shared';

import {
  LayoutEditEvent,
  LayoutTableComponent,
} from '../../components/layout-table/layout-table.component';
import {
  LayoutUploadComponent,
  LayoutUploadDialogData,
} from '../../components/layout-upload/layout-upload.component';
import { RoomHistoryComponent } from '../../components/room-history/room-history.component';
import { RoomsService } from '../../services/rooms.service';

@Component({
  selector: 'bad-room-details',
  imports: [
    RdsIconComponent,
    ReactiveFormsModule,
    LayoutTableComponent,
    RdsLinkComponent,
    MatTimepicker,
    MatTimepickerInput,
    MatTimepickerToggle,
    RdsIconButtonComponent,
    RdsProgressSpinnerComponent,
    RdsFormFieldComponent,
    RdsLabelDirective,
    RdsInputDirective,
    RdsRadioGroupDirective,
    RdsRadioButtonComponent,
    RdsTooltipDirective,
    RdsSwitchComponent,
    RdsButtonComponent,
    RdsTabsComponent,
    RdsTabComponent,
    RoomHistoryComponent,
    RdsChipComponent,
  ],
  templateUrl: './room-details.component.html',
  styleUrl: './room-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomDetailsComponent implements OnInit {
  readonly #roomService = inject(RoomsService);
  readonly #dialogService = inject(RdsDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #fb = inject(FormBuilder);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #isLayoutProcessing = signal(false);
  protected readonly roomDexId =
    this.#activatedRoute.snapshot.paramMap.get('roomDexId')!;
  protected readonly roomLayouts = this.#roomService.roomLayouts;
  protected readonly activeLayouts = computed(() =>
    this.roomLayouts().filter(layout => layout.active)
  );
  protected readonly roomServices = this.#roomService.roomServices;
  protected readonly roomHistory = this.#roomService.roomHistory;
  protected readonly isRoomHistoryLoading =
    this.#roomService.isRoomHistoryLoading;
  protected readonly isLoading = computed(
    () =>
      this.#roomService.isRoomDetailsLoading() ||
      this.#roomService.isRoomLayoutsLoading() ||
      this.#roomService.isRoomServicesLoading() ||
      this.#isLayoutProcessing()
  );
  protected readonly servicesControlsSignal = signal(false);
  /** Only the Details tab has actions, so the bottom bar follows it. */
  protected readonly isDetailsTabActive = signal(true);
  protected roomSettingsSignal = signal<RoomSettingModel | null>(null);
  protected readonly roomConfigForm = this.#fb.group({
    description: this.#fb.nonNullable.control<string>('', [
      Validators.maxLength(4096),
    ]),
    active: this.#fb.nonNullable.control<boolean>(true),
    automaticApproval: this.#fb.nonNullable.control<boolean>(false),
    freezeDays: this.#fb.nonNullable.control<number>(0),
    generalQuestionsEnabled: this.#fb.nonNullable.control<boolean>(false),
    defaultLayoutId: this.#fb.nonNullable.control<string>(''),
    setBlockingTime: this.#fb.nonNullable.control<boolean>(false),
    setMeetingDurationRangeTime: this.#fb.nonNullable.control<boolean>(false),
    blockingTimes: this.#fb.nonNullable.array<
      FormGroup<{
        from: FormControl<string>;
        to: FormControl<string>;
        uuid: FormControl<string | null>;
      }>
    >([]),
    durationRange: this.#fb.nonNullable.group({
      min: this.#fb.group({
        days: this.#fb.nonNullable.control<number>(0, [Validators.min(0)]),
        hours: this.#fb.nonNullable.control<number>(0, [Validators.min(0)]),
        minutes: this.#fb.nonNullable.control<number>(0, [Validators.min(0)]),
      }),
      max: this.#fb.nonNullable.group({
        days: this.#fb.nonNullable.control<number>(0, [Validators.min(0)]),
        hours: this.#fb.nonNullable.control<number>(0, [Validators.min(0)]),
        minutes: this.#fb.nonNullable.control<number>(0, [Validators.min(0)]),
      }),
    }),
    services: this.#fb.group({}),
    tags: this.#fb.nonNullable.control<string[]>([]),
    tagInput: this.#fb.nonNullable.control<string>('', [
      Validators.pattern(/^\S+$/),
    ]),
  });

  public ngOnInit() {
    if (!this.#roomService.rooms()) {
      this.#roomService
        .getRooms(this.#activatedRoute.snapshot.data['role'] as RoleType | null)
        .subscribe();
    }
    this.#roomService
      .getRoomServices()
      .pipe(
        tap(services => this.#createServicesFormControls(services)),
        switchMap(() => this.#roomService.getRoomSettings(this.roomDexId))
      )
      .subscribe(roomSettings => {
        this.roomSettingsSignal.set(roomSettings);
        if (roomSettings.manageSpaceConfiguration) {
          this.#initRoomConfiguration(roomSettings.manageSpaceConfiguration);
        }
      });
    this.#roomService.loadRoomHistory(this.roomDexId).subscribe();
  }

  protected openLayoutUploadDialog() {
    const room = this.#roomService.getRoomByDexId(this.roomDexId);
    const layoutDialog = this.#dialogService.open(LayoutUploadComponent, {
      disableClose: true,
      size: 'm',
      maxHeight: '100%',
      data: {
        roomDexId: this.roomDexId,
        capacity: room?.capacity ?? 0,
      } as LayoutUploadDialogData,
    });

    layoutDialog
      .afterClosed()
      .pipe(
        tap(() => this.#isLayoutProcessing.set(true)),
        takeUntilDestroyed(this.#destroyRef),
        filter(Boolean),
        delay(1000),
        switchMap(() => this.#roomService.getRoomSettings(this.roomDexId)),
        switchMap(roomSettings => {
          const layouts = roomSettings.manageSpaceConfiguration?.layouts ?? [];
          const activeLayouts = layouts.filter(layout => layout.active);
          if (activeLayouts.length === 1) {
            return this.#roomService
              .updateRoomSettings(this.roomDexId, {
                ...roomSettings.manageSpaceConfiguration,
                roomDexId: this.roomDexId,
                defaultLayoutId: activeLayouts[0].layoutDexUUID,
              })
              .pipe(
                tap(updatedSettings => {
                  this.roomSettingsSignal.set(updatedSettings);
                  this.roomConfigForm.controls.defaultLayoutId.patchValue(
                    activeLayouts[0].layoutDexUUID
                  );
                })
              );
          }
          this.roomSettingsSignal.set(roomSettings);
          return of(roomSettings);
        }),
        finalize(() => this.#isLayoutProcessing.set(false))
      )
      .subscribe();
  }

  protected updateRoomConfig() {
    this.roomConfigForm.controls.blockingTimes.updateValueAndValidity();

    const roomSettings: ManageSpaceConfigurationModel = {
      active: this.roomConfigForm.controls.active.value,
      blockingTimes: [],
      defaultLayoutId: this.roomConfigForm.controls.defaultLayoutId.value,
      description: this.roomConfigForm.controls.description.value,
      durationRange: {
        min: {} as RoomSettingsDurationRangeModel,
        max: {} as RoomSettingsDurationRangeModel,
      },
      automaticApproval: this.roomConfigForm.controls.automaticApproval.value,
      roomDexId: this.roomDexId,
      services: [],
      freezeDays: this.roomConfigForm.controls.freezeDays.value,
      generalQuestionsEnabled:
        this.roomConfigForm.controls.generalQuestionsEnabled.value,
      tags: [],
    };

    if (this.roomConfigForm.controls.blockingTimes.length > 0) {
      const blockingTimes = this.roomConfigForm.controls.blockingTimes
        .value as RoomSettingsBlockingTimeModel[];
      blockingTimes.forEach(blockingTime => {
        if (
          (blockingTime.from as unknown) instanceof Date &&
          (blockingTime.from as unknown) instanceof Date
        ) {
          blockingTime.from = format(blockingTime.from, 'HH:mm');
          blockingTime.to = format(blockingTime.to, 'HH:mm');
        }
      });
      roomSettings.blockingTimes = blockingTimes;
    }

    if (this.roomServices().length > 0) {
      this.roomServices().forEach(service => {
        const serviceControl = this.roomConfigForm.controls.services.get(
          service.type
        ) as unknown as FormControl<boolean>;
        if (serviceControl && serviceControl.value) {
          roomSettings.services?.push(service);
        }
      });
    }

    if (this.roomConfigForm.value.setMeetingDurationRangeTime) {
      roomSettings.durationRange = {
        min: {
          ...(this.roomConfigForm.controls.durationRange.controls.min
            .value as RoomSettingsDurationRangeModel),
        },
        max: {
          ...(this.roomConfigForm.controls.durationRange.controls.max
            .value as RoomSettingsDurationRangeModel),
        },
      };
    }

    roomSettings.tags = this.roomConfigForm.controls.tags.value
      .map(tag => tag.trim())
      .filter(Boolean);

    this.#roomService
      .updateRoomSettings(this.roomDexId, roomSettings)
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.goBack();
      });
  }

  protected updateDefaultLayout(layoutId: string) {
    this.roomConfigForm.controls.defaultLayoutId.patchValue(layoutId);
    this.roomConfigForm.markAsDirty();
  }

  protected goBack() {
    this.#router.navigate(['..'], { relativeTo: this.#activatedRoute });
  }

  protected removeLayout(layoutId: string) {
    this.#roomService
      .deleteLayout(layoutId)
      .pipe(
        switchMap(() => this.#roomService.getRoomSettings(this.roomDexId)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(roomSettings => {
        this.roomSettingsSignal.set(roomSettings);
      });
  }

  protected openLayoutEditDialog(event: LayoutEditEvent) {
    const room = this.#roomService.getRoomByDexId(this.roomDexId);
    const layoutDialog = this.#dialogService.open(LayoutUploadComponent, {
      disableClose: true,
      size: 'm',
      maxHeight: '100%',
      data: {
        roomDexId: this.roomDexId,
        capacity: room?.capacity ?? 0,
        editLayout: event.layout,
      } as LayoutUploadDialogData,
    });

    layoutDialog
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(Boolean),
        switchMap(() => this.#roomService.getRoomSettings(this.roomDexId))
      )
      .subscribe(roomSettings => {
        this.roomSettingsSignal.set(roomSettings);
      });
  }

  #initRoomConfiguration(roomConfiguration: ManageSpaceConfigurationModel) {
    this.roomConfigForm.patchValue({
      description: roomConfiguration.description,
      active: roomConfiguration.active,
      automaticApproval: roomConfiguration.automaticApproval,
      defaultLayoutId: roomConfiguration.defaultLayoutId,
      freezeDays: roomConfiguration.freezeDays || 0,
      generalQuestionsEnabled:
        roomConfiguration.generalQuestionsEnabled ?? false,
      tags: roomConfiguration.tags ?? [],
    });

    if (
      roomConfiguration.durationRange &&
      'days' &&
      'hours' &&
      'minutes' in roomConfiguration.durationRange.min
    ) {
      this.roomConfigForm.controls.setMeetingDurationRangeTime.patchValue(true);
      this.roomConfigForm.controls.durationRange.controls.min.patchValue({
        days: roomConfiguration.durationRange.min.days,
        hours: roomConfiguration.durationRange.min.hours,
        minutes: roomConfiguration.durationRange.min.minutes,
      });
    }

    if (
      roomConfiguration.durationRange &&
      'days' &&
      'hours' &&
      'minutes' in roomConfiguration.durationRange.max
    ) {
      this.roomConfigForm.controls.setMeetingDurationRangeTime.patchValue(true);
      this.roomConfigForm.controls.durationRange.controls.max.patchValue({
        days: roomConfiguration.durationRange.max.days,
        hours: roomConfiguration.durationRange.max.hours,
        minutes: roomConfiguration.durationRange.max.minutes,
      });
    }

    if (
      roomConfiguration.blockingTimes &&
      roomConfiguration.blockingTimes.length > 0
    ) {
      this.roomConfigForm.controls.setBlockingTime.patchValue(true);
      roomConfiguration.blockingTimes.forEach(blockingTime => {
        const formGroup = this.#fb.group({
          from: this.#fb.nonNullable.control(
            this.parseTimeStringToDate(blockingTime.from) as string,
            [Validators.required]
          ),
          to: this.#fb.nonNullable.control(
            this.parseTimeStringToDate(blockingTime.to) as string,
            [Validators.required]
          ),
          uuid: this.#fb.control<string | null>(blockingTime.uuid),
        });

        this.roomConfigForm.controls.blockingTimes.push(formGroup);
      });
    }

    if (roomConfiguration.services && roomConfiguration.services.length > 0) {
      roomConfiguration.services.forEach(service => {
        const serviceControl = this.roomConfigForm.controls.services.get(
          service.type
        ) as unknown as FormControl<boolean>;
        if (serviceControl) {
          serviceControl.patchValue(true);
        }
      });
    }
    this.roomConfigForm.markAsPristine();
  }

  protected addBlockingTime() {
    const formGroup = this.#fb.group({
      from: this.#fb.nonNullable.control('', [Validators.required]),
      to: this.#fb.nonNullable.control('', Validators.required),
      uuid: this.#fb.control<string | null>(null),
    });

    this.roomConfigForm.controls.blockingTimes.push(formGroup);
  }

  protected removeBlockingTime() {
    const blockingTimes = this.roomConfigForm.controls.blockingTimes;
    const length = this.roomConfigForm.controls.blockingTimes.length;
    if (length > 1) {
      blockingTimes.removeAt(length - 1);
      this.roomConfigForm.markAsDirty();
    }
  }

  protected toggleRoomBlockingTime(
    // @ts-expect-error ignore
    radioButtonData
  ) {
    if (
      radioButtonData.value &&
      this.roomSettingsSignal()?.manageSpaceConfiguration?.blockingTimes &&
      this.roomSettingsSignal()!.manageSpaceConfiguration!.blockingTimes!
        .length > 0
    ) {
      this.roomSettingsSignal()!.manageSpaceConfiguration!.blockingTimes!.forEach(
        blockingTime => {
          const formGroup = this.#fb.group({
            from: this.#fb.nonNullable.control(blockingTime.from.slice(0, 5), [
              Validators.required,
            ]),
            to: this.#fb.nonNullable.control(
              blockingTime.to.slice(0, 5),
              Validators.required
            ),
            uuid: this.#fb.control<string | null>(blockingTime.uuid),
          });

          this.roomConfigForm.controls.blockingTimes.push(formGroup);
        }
      );

      return;
    }

    if (
      radioButtonData.value &&
      this.roomConfigForm.controls.blockingTimes.length === 0
    ) {
      this.addBlockingTime();

      return;
    }

    if (!radioButtonData.value) {
      this.roomConfigForm.controls.blockingTimes.clear();
    }
  }

  private parseTimeStringToDate(time: string): Date | string {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return '';
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  #createServicesFormControls(services: RoomServicesModel[]) {
    services.forEach(service => {
      this.roomConfigForm.controls.services.addControl(
        service.type,
        this.#fb.nonNullable.control<boolean>(false)
      );
    });
    this.servicesControlsSignal.set(true);
  }

  protected addTag() {
    const tagControl = this.roomConfigForm.controls.tagInput;
    const rawValue = tagControl.value.trim();

    if (!rawValue) {
      return;
    }

    tagControl.updateValueAndValidity({ emitEvent: false });
    if (tagControl.invalid) {
      tagControl.markAsTouched();
      return;
    }

    const tags = this.roomConfigForm.controls.tags.value;
    if (tags.some(tag => tag.toLowerCase() === rawValue.toLowerCase())) {
      tagControl.setErrors({ duplicateTag: true });
      tagControl.markAsTouched();
      return;
    }

    this.roomConfigForm.controls.tags.patchValue([...tags, rawValue]);
    tagControl.setValue('');
    tagControl.setErrors(null);
    this.roomConfigForm.markAsDirty();
  }

  protected removeTag(tagToRemove: string) {
    const nextTags = this.roomConfigForm.controls.tags.value.filter(
      tag => tag.toLowerCase() !== tagToRemove.toLowerCase()
    );
    this.roomConfigForm.controls.tags.patchValue(nextTags);
    this.roomConfigForm.markAsDirty();
  }

  protected onTagInputEnter(event: Event) {
    event.preventDefault();
    this.addTag();
  }
}
